import React, { useState, useEffect } from 'react';
import {  BarChart3, Info, Loader2, Target } from 'lucide-react';
import api from '../../api';

const AttainmentReport = ({ assignmentId }) => {
  const [attainment, setAttainment] = useState(null);
  const [loading, setLoading] = useState(true);


  const loadAttainment = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/faculty/marks-entry/${assignmentId}/attainment`);
      setAttainment(res.data.data);
    } catch (error) {
      console.error('Error loading attainment:', error);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    loadAttainment();
  }, [assignmentId, loadAttainment]);

  if (loading) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-xs">
        <Loader2 className="w-7 h-7 text-blue-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500 font-medium text-xs">Compiling direct CO attainment vectors...</p>
      </div>
    );
  }

  if (!attainment || Object.keys(attainment.coAttainment || {}).length === 0) {
    return (
      <div className="w-full bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-xs">
        <div className="w-12 h-12 bg-slate-50 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
          <BarChart3 className="w-5 h-5 text-gray-400" />
        </div>
        <h3 className="text-sm font-bold text-gray-800">Insufficient Dataset</h3>
        <p className="text-xs text-gray-400 max-w-xs mx-auto mt-1 leading-relaxed">
          No student evaluation weights were located to perform outcome mapping computations.
        </p>
      </div>
    );
  }

  const getAttainmentLevel = (percentage) => {
    if (percentage >= 80) return { level: 'High', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', barColor: 'bg-emerald-500' };
    if (percentage >= 60) return { level: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200', barColor: 'bg-amber-500' };
    return { level: 'Low', color: 'bg-rose-50 text-rose-700 border-rose-200', barColor: 'bg-rose-500' };
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-2xs overflow-hidden font-sans antialiased">
      
      {/* Structural Header Area */}
      <div className="p-4 md:p-5 bg-slate-50/50 border-b border-gray-200 flex items-center gap-2.5">
        <div className="h-9 w-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center text-blue-600 shrink-0">
          <Target className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-extrabold text-sm md:text-base text-gray-900 tracking-tight">
            Course Outcome (CO) Attainment
          </h3>
          <p className="text-[11px] text-gray-400 font-medium mt-0.5">Outcome target evaluation matrix records</p>
        </div>
      </div>

      {/* Main Analysis Content Map */}
      <div className="p-4 md:p-6 space-y-5 md:space-y-6">
        <div className="grid grid-cols-1 gap-4 md:gap-5">
          {Object.entries(attainment.coAttainment).map(([co, value]) => {
            const { level, color, barColor } = getAttainmentLevel(value);
            return (
              <div key={co} className="p-3.5 md:p-4 border border-gray-100 rounded-xl bg-slate-50/30 hover:bg-slate-50/70 transition-colors shadow-3xs">
                
                {/* Meta Summary labels */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                  <span className="font-bold text-xs text-gray-900 tracking-wide uppercase bg-white border border-gray-200 px-2.5 py-1 rounded-md shadow-3xs shrink-0 self-start sm:self-auto">
                    {co}
                  </span>
                  
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <span className={`px-2 py-0.5 rounded-md font-mono font-black text-[10px] border shadow-3xs uppercase tracking-wider ${color}`}>
                      {level}
                    </span>
                    <span className="text-sm font-black text-gray-800 bg-white border border-gray-100 px-2 py-0.5 rounded-md shadow-3xs">
                      {value.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Progress Tracking Bar Rail */}
                <div className="w-full bg-slate-100 border border-gray-200/60 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`${barColor} h-full rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${Math.min(value, 100)}%` }}
                  />
                </div>

              </div>
            );
          })}
        </div>

        {/* Informative Roster Metadata Note */}
        <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 flex items-start gap-2.5 text-xs text-blue-800 leading-relaxed">
          <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <strong className="font-bold">Computation Framework Note:</strong> Attainment parameters are mapped cleanly across <b className="font-black text-blue-900">{attainment.totalStudentsConsidered}</b> student profiles displaying processed assessment entries.
          </div>
        </div>

      </div>
    </div>
  );
};

export default AttainmentReport;